import { stringArrayToObject } from '../../utils/array';

import { actionCreators as toastActions } from '../Toasts/actions';

import I18n from '../../config/i18n';

import { IOS_PLATFORM } from '../../utils/platforms';

import {
  updateProduct,
  updateProductImage,
  updateProductBundle,
  getProductDetails,
  assignProduct,
  dissociateProduct,
  createNewProductQR,
  updateProductOverride,
} from '../../services/ProductServices';

export const actions = stringArrayToObject(
  [
    'CLEAR',
    'SET_PRODUCT',
    'LOAD_START',
    'LOAD_FAIL',
    'UPDATE_START',
    'UPDATE_SUCCESS',
    'UPDATE_FAIL',
    'IMAGE_UPDATE_START',
    'IMAGE_UPDATE_SUCCESS',
    'IMAGE_UPDATE_FAIL',
    'BUNDLE_UPDATE_START',
    'BUNDLE_UPDATE_SUCCESS',
    'BUNDLE_UPDATE_FAIL',
    'ASSIGN_CHANGE_START',
    'ASSIGN_CHANGE_SUCCESS',
    'ASSIGN_CHANGE_FAIL',
    'UPDATE_QR',
    'NEW_OVERRIDE',
    'EDIT_OVERRIDE',
    'CLOSE_OVERRIDE',
  ],
  '@@EDIT_PRODUCT_FORM',
);

const privateActionCreators = {
  setProduct(product) {
    return {
      type: actions.SET_PRODUCT,
      payload: { product },
    };
  },
  loadStart() {
    return {
      type: actions.LOAD_START,
    };
  },
  loadFail(error) {
    return {
      type: actions.LOAD_FAIL,
      payload: { error },
    };
  },
  startUpdate() {
    return {
      type: actions.UPDATE_START,
    };
  },
  updateSuccess(product) {
    return {
      type: actions.UPDATE_SUCCESS,
      payload: { product },
    };
  },
  updateFail(error) {
    return {
      type: actions.UPDATE_FAIL,
      payload: { error },
    };
  },
  updateImageStart(index) {
    return {
      type: actions.IMAGE_UPDATE_START,
      payload: { index },
    };
  },
  updateImageSuccess(index, data) {
    return {
      type: actions.IMAGE_UPDATE_SUCCESS,
      payload: { index, data },
    };
  },
  updateImageFail(index, error) {
    return {
      type: actions.BUNDLE_UPDATE_FAIL,
      payload: { index, error },
    };
  },
  updateBundleStart(platform = IOS_PLATFORM) {
    return {
      type: actions.BUNDLE_UPDATE_START,
      payload: { platform },
    };
  },
  updateBundleSuccess(data, platform = IOS_PLATFORM) {
    return {
      type: actions.BUNDLE_UPDATE_SUCCESS,
      payload: { data, platform },
    };
  },
  updateBundleFail(error, platform = IOS_PLATFORM) {
    return {
      type: actions.BUNDLE_UPDATE_FAIL,
      payload: { error, platform },
    };
  },
  assignChangeStart() {
    return {
      type: actions.ASSIGN_CHANGE_START,
    };
  },
  assignChangeSuccess(product) {
    return {
      type: actions.ASSIGN_CHANGE_SUCCESS,
      payload: { product },
    };
  },
  assignChangeFail(error) {
    return {
      type: actions.ASSIGN_CHANGE_FAIL,
      payload: { error },
    };
  },
  updateQR(qr) {
    return {
      type: actions.UPDATE_QR,
      payload: { qr },
    };
  },
};

export const actionCreators = {
  clearForm() {
    return {
      type: actions.CLEAR,
    };
  },
  updateProductInfo(id, info) {
    return async (dispatch) => {
      dispatch(privateActionCreators.startUpdate());
      const response = await updateProduct(id, info);
      if (response.ok) {
        dispatch(toastActions.addToast({
          type: 'success',
          message: I18n.t('forms.editProduct.messages.infoUpdated'),
        }));
        dispatch(privateActionCreators.updateSuccess(response.product));
      } else {
        dispatch(toastActions.addToast({
          type: 'error',
          message: I18n.t('forms.editProduct.errors.infoFailed'),
        }));
        dispatch(privateActionCreators.updateFail(response.error));
      }
    };
  },
  updateProductImage(id, { file, index }) {
    return async (dispatch) => {
      if (!file) {
        dispatch(toastActions.addToast({
          type: 'success',
          message: I18n.t('forms.editProduct.errors.noImage', { index: index + 1 }),
        }));
        return;
      }
      dispatch(privateActionCreators.updateImageStart(index));
      const response = await updateProductImage(id, { file, index });
      if (response.ok) {
        dispatch(toastActions.addToast({
          type: 'success',
          message: I18n.t('forms.editProduct.messages.imageUpdated', { index: index + 1 }),
        }));
        dispatch(privateActionCreators.updateImageSuccess(index, response.data));
      } else {
        dispatch(toastActions.addToast({
          type: 'error',
          message: I18n.t('forms.editProduct.errors.imageFailed', { index: index + 1 }),
        }));
        dispatch(privateActionCreators.updateImageFail(index, response.error));
      }
    };
  },
  updateProductBundle(id, mesh, platform = IOS_PLATFORM) {
    return async (dispatch) => {
      if (!mesh) {
        dispatch(toastActions.addToast({
          type: 'error',
          message: I18n.t('forms.editProduct.errors.noBundle'),
        }));
        return;
      }
      dispatch(privateActionCreators.updateBundleStart(platform));
      const response = await updateProductBundle(id, { mesh, platform });
      if (response.ok) {
        dispatch(toastActions.addToast({
          type: 'success',
          message: I18n.t('forms.editProduct.messages.bundleUpdated'),
        }));
        dispatch(privateActionCreators.updateBundleSuccess(response.data, platform));
      } else {
        dispatch(toastActions.addToast({
          type: 'error',
          message: I18n.t('forms.editProduct.errors.bundleFailed'),
        }));
        dispatch(privateActionCreators.updateBundleFail(response.error, platform));
      }
    };
  },
  getProductDetails(id, { owner = null } = {}) {
    return async (dispatch) => {
      dispatch(privateActionCreators.loadStart());
      const response = await getProductDetails(id, { owner });
      if (response.ok) {
        dispatch(privateActionCreators.setProduct(response.product));
      } else {
        dispatch(privateActionCreators.loadFail(response.error));
      }
    };
  },
  associateProduct(id, override) {
    return async (dispatch) => {
      dispatch(privateActionCreators.assignChangeStart());
      const response = await assignProduct(id, override);
      if (response.ok) {
        dispatch(toastActions.addToast({
          type: 'success',
          message: I18n.t('screens.products.details.messages.assigned'),
        }));
        dispatch(privateActionCreators.assignChangeSuccess(response.product));
      } else {
        dispatch(toastActions.addToast({
          type: 'error',
          message: I18n.t('screens.products.details.errors.notAssigned'),
        }));
        dispatch(privateActionCreators.assignChangeFail(response.error));
      }
    };
  },
  updateProductOverride(id, override) {
    return async (dispatch) => {
      const response = await updateProductOverride(id, override);
      if (response.ok) {
        dispatch(toastActions.addToast({
          type: 'success',
          message: I18n.t('screens.products.details.messages.updated'),
        }));
        dispatch(privateActionCreators.assignChangeSuccess(response.product));
      } else {
        dispatch(toastActions.addToast({
          type: 'error',
          message: I18n.t('screens.products.details.errors.notUpdated'),
        }));
        dispatch(privateActionCreators.assignChangeFail(response.error));
      }
    };
  },
  dissociateProduct(id) {
    return async (dispatch) => {
      dispatch(privateActionCreators.assignChangeStart());
      const response = await dissociateProduct(id);
      if (response.ok) {
        dispatch(toastActions.addToast({
          type: 'success',
          message: I18n.t('screens.products.details.messages.dissociated'),
        }));
        dispatch(privateActionCreators.assignChangeSuccess(response.product));
      } else {
        dispatch(toastActions.addToast({
          type: 'error',
          message: I18n.t('screens.products.details.errors.notDissociated'),
        }));
        dispatch(privateActionCreators.assignChangeFail(response.error));
      }
    };
  },
  generateProductQR(id) {
    return async (dispatch) => {
      const response = await createNewProductQR(id);
      if (response.ok) {
        dispatch(toastActions.addToast({
          type: 'success',
          message: I18n.t('screens.products.details.messages.qrGenerated'),
        }));
        dispatch(privateActionCreators.updateQR(response.url));
      } else {
        dispatch(toastActions.addToast({
          type: 'error',
          message: I18n.t('screens.products.details.errors.qrFailed'),
        }));
      }
    };
  },
  openNewOverride() {
    return {
      type: actions.NEW_OVERRIDE,
    };
  },
  editOverride() {
    return {
      type: actions.EDIT_OVERRIDE,
    };
  },
  closeOverrideModal() {
    return {
      type: actions.CLOSE_OVERRIDE,
    };
  },
};



// WEBPACK FOOTER //
// ./src/redux/EditProductForm/actions.js